import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Download, Loader2, Plus, FileText, CheckCircle, Trash2, Clock, Pencil } from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import ManualInvoiceDialog from "@/components/crm/ManualInvoiceDialog";
import useConfirm from "@/components/crm/useConfirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LeistungsdatumPicker, {
  emptyLeistungsdatum, formatLeistungsdatum, validateLeistungsdatum,
} from "@/components/crm/LeistungsdatumPicker";

const STATUS_LABEL = {
  entwurf: "Entwurf",
  versendet: "Versendet",
  bezahlt: "Bezahlt",
  ueberfaellig: "Überfällig",
  storniert: "Storniert",
};

const STATUS_STYLE = {
  entwurf: "bg-[#E8ECE1] text-[#2C3E2D]",
  versendet: "bg-[#DBE7F1] text-[#1F3B54]",
  bezahlt: "bg-[#DCEBD4] text-[#3B5C2C]",
  ueberfaellig: "bg-[#F5D9CC] text-[#7A2E14]",
  storniert: "bg-slate-100 text-slate-500",
};

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmt(v) {
  return (Math.round(v * 100) / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [fromQuoteDlg, setFromQuoteDlg] = useState(false);
  const [manualDlg, setManualDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [i, q] = await Promise.all([api.get("/invoices"), api.get("/quotes")]);
      setItems(i.data);
      setQuotes(q.data.filter((x) => ["entwurf", "versendet", "angenommen"].includes(x.status)));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openPdf = async (id) => {
    const token = localStorage.getItem("blessing_token");
    setBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } finally { setBusyId(null); }
  };

  const send = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/invoices/${id}/send`);
      if (r.data.email.status === "simulated") {
        toast.warning("Rechnung markiert als versendet – E-Mail NICHT gesendet (Microsoft Graph nicht konfiguriert)");
      } else if (r.data.email.status === "error") {
        toast.error("Rechnung markiert, aber E-Mail-Versand über Microsoft Graph fehlgeschlagen");
      } else {
        toast.success("Rechnung versendet");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/invoices/${id}/status`, { status });
      toast.success("Status aktualisiert");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createFromQuote = async (quoteId, extras) => {
    try {
      await api.post(`/invoices/from-quote/${quoteId}`, extras);
      toast.success("Rechnung aus Angebot erstellt");
      setFromQuoteDlg(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (inv) => {
    if (busyId) return;
    const ok = await confirm({
      title: `Rechnung ${inv.invoice_number} löschen?`,
      description: "Diese Aktion kann nicht rückgängig gemacht werden. Die Rechnungsnummer wird für die nächste Rechnung wieder frei.",
    });
    if (!ok) return;
    setBusyId(inv.id);
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Rechnung gelöscht");
    } catch (e) {
      // 404 = bereits gelöscht (z. B. Doppeltipp oder zweites Gerät) – Liste trotzdem aktualisieren.
      if (e?.response?.status !== 404) { toast.error(formatApiError(e)); return; }
    } finally { setBusyId(null); }
    setItems((prev) => prev.filter((x) => x.id !== inv.id));
    load();
  };

  return (
    <div data-testid="crm-invoices">
      <div className="mb-5 md:mb-8 flex items-start justify-between gap-3 md:gap-4 flex-wrap">
        <div>
          <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">CRM</div>
          <h1 className="hidden md:block font-display text-3xl font-semibold text-[#1A201A] tracking-tight">Rechnungen</h1>
          <p className="text-sm text-[#5C6B5D] mt-1">Rechnungen erzeugen, versenden und Zahlungseingang verfolgen.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            className="border-[#6B7F3E] text-[#2C3E2D] hover:bg-[#F5F6F0]"
            onClick={() => setManualDlg(true)}
            data-testid="invoice-manual-btn"
          >
            <Clock className="w-4 h-4 mr-1" /> Manuelle Rechnung
          </Button>
          <Button
            className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
            onClick={() => setFromQuoteDlg(true)}
            data-testid="invoice-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Aus Angebot erstellen
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        {loading && <div className="p-8 text-sm text-[#5C6B5D]">Lädt…</div>}
        {!loading && items.length === 0 && (
          <div className="p-16 text-center text-sm text-[#5C6B5D]">
            Noch keine Rechnungen vorhanden. Aus einem Angebot in wenigen Sekunden anlegen.
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="crm-table w-full text-sm">
              <thead className="bg-[#F5F6F0] text-[11px] uppercase tracking-wider text-[#5C6B5D]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Nr.</th>
                  <th className="text-left px-4 py-3 font-medium">Kunde</th>
                  <th className="text-right px-4 py-3 font-medium">Betrag</th>
                  <th className="text-left px-4 py-3 font-medium">Fällig</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-right px-4 py-3 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-t border-[#E5E7EB] hover:bg-[#FAFAF5]">
                    <td className="px-4 py-3 font-mono text-xs" data-label="Nr.">{inv.invoice_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{inv.customer_name}</div>
                      {(inv.items || []).some((it) => it.hourly) && (
                        <div className="text-[11px] text-[#5C6B5D] flex items-center gap-1 mt-0.5" data-testid={`invoice-hourly-${inv.id}`}>
                          <Clock className="w-3 h-3" /> Stundenabrechnung
                          {inv.hourly_rate ? ` · ${fmt(inv.hourly_rate)}/Std.` : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium" data-label="Betrag">{fmt(inv.total)}</td>
                    <td className="px-4 py-3 text-[#4A5D4E]" data-label="Fällig">{inv.due_date || "—"}</td>
                    <td className="px-4 py-3" data-label="Status">
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[inv.status] || "bg-slate-100"}`}>
                          {STATUS_LABEL[inv.status]}
                        </span>
                        {inv.status !== "bezahlt" && inv.status !== "storniert" && (
                          <Select value="" onValueChange={(v) => changeStatus(inv.id, v)}>
                            <SelectTrigger className="h-7 text-[11px] w-[120px]" data-testid={`invoice-status-${inv.id}`}>
                              <SelectValue placeholder="Ändern…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bezahlt">Bezahlt</SelectItem>
                              <SelectItem value="storniert">Storniert</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 crm-td-actions">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openPdf(inv.id)} disabled={busyId === inv.id} data-testid={`invoice-pdf-${inv.id}`}>
                          {busyId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(inv)}
                          disabled={busyId === inv.id}
                          data-testid={`invoice-edit-${inv.id}`}
                          aria-label="Rechnung bearbeiten"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
                          onClick={() => send(inv.id)}
                          disabled={busyId === inv.id}
                          data-testid={`invoice-send-${inv.id}`}
                        >
                          {busyId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Versenden</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => remove(inv)}
                          disabled={busyId === inv.id}
                          className="border-[#F5D9CC] text-[#B85C38] hover:bg-[#F5D9CC] hover:text-[#7A2E14]"
                          data-testid={`invoice-delete-${inv.id}`}
                          aria-label="Rechnung löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={fromQuoteDlg} onOpenChange={setFromQuoteDlg}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display">Rechnung aus Angebot</DialogTitle>
          </DialogHeader>
          <FromQuoteForm
            quotes={quotes}
            onCancel={() => setFromQuoteDlg(false)}
            onCreate={createFromQuote}
          />
        </DialogContent>
      </Dialog>

      <ManualInvoiceDialog open={manualDlg} onOpenChange={setManualDlg} onCreated={load} />
      <ManualInvoiceDialog
        open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        onCreated={load}
        invoice={editing}
      />
      {confirmDialog}
    </div>
  );
}

function FromQuoteForm({ quotes, onCancel, onCreate }) {
  const [quoteId, setQuoteId] = useState("");
  const [leistungsdatum, setLeistungsdatum] = useState(emptyLeistungsdatum());
  const [ausstellung, setAusstellung] = useState(todayIso());
  const [startNumber, setStartNumber] = useState("");
  const [kleinunternehmer, setKleinunternehmer] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!quoteId) { toast.error("Bitte ein Angebot wählen"); return; }
    const leistungsdatumError = validateLeistungsdatum(leistungsdatum);
    if (leistungsdatumError) { toast.error(leistungsdatumError); return; }
    if (!ausstellung) { toast.error("Bitte Ausstellungsdatum wählen"); return; }
    const extras = {
      ausstellungsdatum: ausstellung,
      rechnungszeitraum: formatLeistungsdatum(leistungsdatum),
    };
    const parsedStart = parseInt(startNumber, 10);
    if (startNumber && (!Number.isFinite(parsedStart) || parsedStart <= 0)) {
      toast.error("Startnummer muss eine positive Zahl sein"); return;
    }
    if (parsedStart > 0) extras.start_number = parsedStart;
    if (kleinunternehmer) extras.kleinunternehmer = true;
    setBusy(true);
    try {
      await onCreate(quoteId, extras);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-wider text-[#5C6B5D]">Angebot</Label>
        <div className="mt-1.5 max-h-56 overflow-y-auto border border-[#E5E7EB] rounded-lg divide-y divide-[#E5E7EB]">
          {quotes.length === 0 ? (
            <div className="text-sm text-[#5C6B5D] p-3">Keine geeigneten Angebote vorhanden.</div>
          ) : quotes.map((q) => (
            <button
              key={q.id}
              type="button"
              onClick={() => setQuoteId(q.id)}
              data-testid={`invoice-from-quote-${q.id}`}
              className={`w-full text-left py-2 px-3 flex items-center justify-between hover:bg-[#F5F6F0] ${
                quoteId === q.id ? "bg-[#E8ECE1]" : ""
              }`}
            >
              <div>
                <div className="text-sm font-medium">{q.quote_number}</div>
                <div className="text-xs text-[#5C6B5D]">{q.customer_name}</div>
              </div>
              <div className="text-sm font-mono">
                {(Math.round(q.total * 100) / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs uppercase tracking-wider text-[#5C6B5D]">Leistungsdatum *</Label>
          <div className="mt-1.5">
            <LeistungsdatumPicker
              value={leistungsdatum}
              onChange={setLeistungsdatum}
              testIdPrefix="invoice-leistungsdatum"
            />
          </div>
        </div>
        <div className="col-span-2">
          <Label htmlFor="ausstellung" className="text-xs uppercase tracking-wider text-[#5C6B5D]">Ausstellungsdatum</Label>
          <Input
            id="ausstellung"
            type="date"
            className="mt-1.5"
            value={ausstellung}
            onChange={(e) => setAusstellung(e.target.value)}
            data-testid="invoice-ausstellung-input"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="start-number" className="text-xs uppercase tracking-wider text-[#5C6B5D]">
            Rechnungsnummer neu setzen (optional)
          </Label>
          <Input
            id="start-number"
            type="number"
            min="1"
            step="1"
            className="mt-1.5"
            placeholder="z. B. 100 – diese Rechnung wird R-{Jahr}-0100"
            value={startNumber}
            onChange={(e) => setStartNumber(e.target.value)}
            data-testid="invoice-start-number-input"
          />
          <div className="mt-1 text-[11px] text-[#5C6B5D]">
            Setzt die fortlaufende Rechnungsnummer zurück – weitere Rechnungen zählen von hier aus weiter.
            Leer lassen, um wie bisher fortzufahren.
          </div>
        </div>
        <div className="col-span-2">
          <label
            htmlFor="invoice-kleinunternehmer"
            className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6F0] px-3 py-2.5 cursor-pointer hover:border-[#6B7F3E]/60 transition"
            data-testid="invoice-kleinunternehmer-row"
          >
            <Checkbox
              id="invoice-kleinunternehmer"
              checked={kleinunternehmer}
              onCheckedChange={(v) => setKleinunternehmer(v === true)}
              data-testid="invoice-kleinunternehmer-checkbox"
              className="mt-0.5"
            />
            <div className="text-sm text-[#2C3E2D] leading-snug">
              <div className="font-medium">Keine Mehrwertsteuer (§ 19 UStG)</div>
              <div className="text-xs text-[#5C6B5D] mt-0.5">
                Aktiviert: MwSt. wird auf 0 % gesetzt und die Klausel „Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." erscheint auf der Rechnung.
                Ohne Aktivierung wird der MwSt-Satz aus dem Angebot übernommen.
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={busy}>Abbrechen</Button>
        <Button
          className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
          onClick={submit}
          disabled={busy || !quoteId}
          data-testid="invoice-create-submit-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rechnung erstellen"}
        </Button>
      </div>
    </div>
  );
}
